import { Cart } from '../types';

const CART_ITEMS_URL = '/services_api/storefront/v1/cart/items.json';

/**
 * Puts a sku in the cart and answers the cart it produced.
 *
 * The first write path V2 has: everything before this read. Two things about the
 * endpoint are worth knowing before calling it.
 *
 * There is always a cart. `routines/cart.php` runs on the way into every request that
 * reaches this api — app.php includes it for anything under
 * `/services_api/storefront/v1/cart` — and it creates one and sets the cookie, so a
 * visitor whose very first act on the site is this button already has a cart by the
 * time the request arrives. A cart here is the session, not the basket.
 *
 * An `offer_code` the cart rejects is *swallowed*: AddCartItemAction catches the
 * failure and the item lands undiscounted, with a 200 and a cart that looks fine. That
 * is why the code sent here is the one the page already proved applies, rather than
 * whatever was in the url — see MagzterGoldPagePropsBuilder, which nulls it out when it
 * bought nothing.
 *
 * The response is serialized with `storefront` + `storefront-cart`, a superset of the
 * summary `Cart` the provider holds, so it can be written straight back.
 *
 * `paymentPayload` is the express-checkout path: a tokenized Apple Pay or Google Pay
 * result, JSON-encoded. AddCartItemAction treats it as "this is the whole order" — it
 * *empties the cart* before adding the sku, then hangs the payload off the order info
 * for checkout to charge. So it is not an extra argument to an ordinary add; it is a
 * different operation, and the only correct thing to do after one is to send the
 * visitor to `cart.checkout_url` with `&xPayload=Y`.
 */
export default async function addItem(
  sku: string,
  offerCode?: string | null,
  paymentPayload?: string | null
): Promise<Cart> {
  const response = await fetch(CART_ITEMS_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sku,
      offer_code: offerCode || undefined,
      payment_payload: paymentPayload || undefined,
    }),
  });

  if (!response.ok) {
    throw new Error(await failureMessage(response));
  }

  return (await response.json()) as Cart;
}

/**
 * The reason the cart gave, when it gave one.
 *
 * Worth reading rather than reporting the status code: `AddCartItemAction` rethrows
 * `cartAddItem`'s own error string — `OUTOFSTOCK`, `LIMITEDSTOCK` — and the serialized
 * form errors carry it out under `errors`. A caller that only ever sees "failed with
 * 400" cannot tell a sold-out book from a broken request, which is the difference
 * between "this is gone" and "try again".
 */
async function failureMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    const reason = Array.isArray(body?.errors) ? body.errors[0] : body?.message;

    if (reason) {
      return String(reason);
    }
  } catch (error) {
    // Not JSON, or empty. The status is all there is.
  }

  return `Add to cart failed with ${response.status}`;
}
